<template>
  <div class="scheduled-tasks__wrapper">
    <BaseButton isSmall isTransparent isSquare @click.native="toggle">
      <div class="scheduled-tasks__trigger">
        <BaseCircularProgress indeterminate :label="items.length.toString()" />
        {{ $tc('navigation-bar.tasks-running', items.length) }}
      </div>
    </BaseButton>

    <div v-if="isOpen" v-closable="close" class="scheduled-tasks">
      <div class="scheduled-tasks__header">
        <span class="scheduled-tasks__title">{{ $t('tasks-queue.queue') }}</span>
        <span class="scheduled-tasks__count">{{ items.length }}</span>
      </div>

      <ul class="scheduled-tasks__body" role="list">
        <li v-for="(item, idx) in items" :key="idx" class="scheduled-tasks__row">
          <!-- Left icon -->
          <div class="scheduled-tasks__row-icon" :class="iconClass(item.status)">
            <BaseCircularProgress :progress-percent="item.percent" progress-color="#f6f7f8" :size="36" />
          </div>

          <!-- Main content -->
          <div class="scheduled-tasks__row-body">
            <div class="scheduled-tasks__row-title">{{ item.title }}</div>
            <div class="scheduled-tasks__row-meta">
              <span>{{ item.subtitle }}</span>

              <!-- status segment -->
              <template v-if="item.status === 'inProgress'">
                <span>{{ Math.max(0, Math.min(100, Math.round(item.percent))) }}%</span>
              </template>
              <template v-else>
                <span>{{ statusLabel(item) }}</span>
              </template>
            </div>
          </div>
        </li>
      </ul>
    </div>
  </div>
</template>

<script>
import BaseButton from '@/components/base/BaseButton.vue';
import BaseCircularProgress from '@/components/base/BaseCircularProgress.vue';

export default {
  name: 'BaseScheduledTasks',
  components: { BaseCircularProgress, BaseButton },
  props: {
    /**
     * items: Array<{
     *   title: string
     *   subtitle: string
     *   status: 'done' | 'inProgress'
     *   percent?: number   // for inProgress
     *   error?: string // for error
     * }>
     */
    items: {
      type: Array,
      required: true,
      default: () => [],
    },
  },
  data() {
    return {
      isOpen: false,
    };
  },
  methods: {
    toggle() {
      this.isOpen = !this.isOpen;
    },
    close() {
      if (this.isOpen) {
        this.isOpen = false;
      }
    },
    statusLabel(item) {
      switch (item.status) {
        case 'done':
          return this.$t('tasks-queue.status.done');
        case 'inProgress':
          return `${Math.round(item.percent)}%`;
        case 'pending':
          return this.$t('tasks-queue.status.pending');
        case 'error':
          return item.error || this.$t('error.unknown-error');
        default:
          return '';
      }
    },
    iconClass(status) {
      return {
        'scheduled-tasks__row-icon-done': status === 'done',
        'scheduled-tasks__row-icon-default': status !== 'done',
      };
    },
  },
  computed: {
    progressPercent() {
      return 100 / this.items.length;
    },
  },
};
</script>

<style lang="scss" scoped>
.scheduled-tasks {
  z-index: $nav-bar-index;
  background: $color-white;
  border-radius: 16px;
  padding: 16px;
  box-shadow: $shadow-dropdown;
  width: 420px;
  position: absolute;
  top: 40px;
  right: 0;

  &__wrapper {
    position: relative;
  }
  &__trigger {
    display: flex;
    align-items: center;
    gap: 8px;
  }
  &__header {
    display: flex;
    align-items: center;
    gap: 8px;
  }
  &__title {
    font-size: $font-size-xbigger;
    font-weight: 700;
    margin: 0;
    color: $color-normal;
    line-height: 36px;
  }
  &__count {
    font-size: $font-size-xsmall;
    font-weight: 600;
    border-radius: $border-radius-round;
    background: $color-light-grey;
    color: $color-normal;
    width: 20px;
    height: 20px;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  &__body {
    list-style: none;
    margin: 0;
    padding: 0;
    max-height: 404px;
    overflow-y: auto;
  }

  &__row {
    display: grid;
    grid-template-columns: 48px 1fr;
    gap: 16px;
    align-items: center;
    padding: 12px;
    border-top: 1px solid $color-border;
    margin: 10px 0;

    &:first-child {
      border-top: none;
    }

    &-icon {
      width: 48px;
      height: 48px;
      border-radius: 12px;
      display: grid;
      place-items: center;

      &-default {
        background: $color-normal;
        color: #eef2ff;
      }
      &-done {
        background: #ebfaf5;
        color: $color-green;
      }
    }

    &-body {
      overflow: hidden;
    }
    &-title {
      font-weight: 700;
      color: $color-normal;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
    &-meta {
      margin-top: 4px;
      font-size: $font-size-small;
      color: $color-normal-pale;
      display: flex;
      flex-direction: column;
      gap: 4px;
      justify-content: flex-start;
    }
  }
}
</style>
